import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Socket, io } from 'socket.io-client';
import CanvasDrawing from '../../components/canvas-drawing/CanvasDrawing';
import { useAuthContext } from '../../context/AuthContext';
import useLocalStorage from '../../hooks/useLocalStorage';
import { decrypt } from '../../utils/cryptojs-helper';

type DrawingPresenceParticipant = {
  socketId: string;
  userId: string;
  displayName: string;
  color: string;
  joinedAt: number;
};

const CanvasDrawingPage = () => {
  const { userNew } = useAuthContext();
  const navigate = useNavigate();
  const needToExitDrawMode = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const [centerMessage, setCenterMessage] = useState<string>('Loading...');
  const [collaboratorCount, setCollaboratorCount] = useState<number>(1);
  const [participants, setParticipants] = useState<DrawingPresenceParticipant[]>([]);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  const [queuedOps, setQueuedOps] = useState<any[]>([]);
  // we need to update the "isInDrawMode" status of the SiteLocation to
  // block any other user from drawing on the map at the same time
  // once that status has updated we will then show case the drawing component
  const [isInDrawModeStatusChange, setIsInDrawModeStatusChange] = useState<boolean | null>(null);
  // we need to check to see if the localstorage has the siteId
  // const siteId = location.pathname.split('/')[2];
  const [drawSiteId, setDrawSiteId] = useLocalStorage('draw_site', {} as { siteId: string; siteName: string });
  const siteId = decrypt(drawSiteId.siteId).slice(1, -1);
  const siteName = drawSiteId.siteName;
  // the following is for tracking the tab visibility
  const handleVisibilityChange = async () => {
    const socket = socketRef.current;
    if (!socket) return;
    // if the window is hidden then we need to change the sitelocation isInDrawMode to false
    // this is needed for the purpose of closing the tab
    if (document.visibilityState === 'hidden') {
      // API_WHITEBOARD_APP_DRAWING.updateWBMapDrawingAvailability({locationId: siteId, status: false});
      socket.emit('hidden-window');
    } else {
      setIsInDrawModeStatusChange(false);
      socket.emit('visible-window');
    }
  };

  // we need to call the function to update the status draw mode
  useEffect(() => {
    const decrypted = decrypt(userNew);
    const userJSON = JSON.parse(decrypted);
    const { currentClient, id } = userJSON;
    // updateSiteLocationIsInDrawModeStatusToTrue();
    const authToken = `${id}*${currentClient.client.id}*${siteId}`;
    // socket. io
    // we need to connect to the drawing socket IO. When we connect it'll
    // let us know if the drawing is currently being used
    // const socketURLEnv = `${process.env.REACT_APP_API_PUBLIC_BASE_URL}/drawing`;
    // console.log('env: ', socketURLEnv);
    const socketURL = process.env.REACT_APP_API_PUBLIC_BASE_URL
      ? process.env.REACT_APP_API_PUBLIC_BASE_URL
      : 'https://curiocloudstaging.creatingmargin.com';
    const socketPath = process.env.REACT_APP_SOCKET_IO_PATH
      ? process.env.REACT_APP_SOCKET_IO_PATH
      : '/api/socket.io';
    const normalizedSocketURL = socketURL.replace(/\/$/, '');
    const socketNamespaceURL = normalizedSocketURL.endsWith('/server')
      ? normalizedSocketURL
      : `${normalizedSocketURL}/server`;

    const socket: Socket = io(socketNamespaceURL, {
      path: socketPath,
      transports: ['websocket', 'polling'],
      auth: { token: authToken },
    }); // Replace 'http://localhost:4000' with your server URL
    socketRef.current = socket;

    // Handle other socket events here
    socket.on('connect_error', (error: Error) => {
      setCenterMessage(`Failed to connect: ${error.message}`);
      // then redirect
      setTimeout(() => navigate('/map-whiteboard'), 5000);
    });

    socket.on('wb:joined', (payload: { snapshot?: any; queuedOps?: any[]; count?: number; participants?: DrawingPresenceParticipant[] }) => {
      if (Object.prototype.hasOwnProperty.call(payload || {}, 'snapshot')) {
        setSnapshotData(payload?.snapshot || null);
      }

      if (Array.isArray(payload?.queuedOps)) {
        setQueuedOps(payload.queuedOps);
      }

      if (typeof payload?.count === 'number') {
        setCollaboratorCount(Math.max(1, payload.count));
      }

      if (Array.isArray(payload?.participants)) {
        setParticipants(payload.participants);
        setCollaboratorCount(Math.max(1, payload.participants.length));
      }
    });

    socket.on('presence', (payload: { count: number; participants?: DrawingPresenceParticipant[] }) => {
      if (payload && typeof payload.count === 'number') {
        setCollaboratorCount(Math.max(1, payload.count));
      }

      if (Array.isArray(payload?.participants)) {
        setParticipants(payload.participants);
        setCollaboratorCount(Math.max(1, payload.participants.length));
      }
    });

    socket.on('wb:presence:update', (payload: { count: number; participants?: DrawingPresenceParticipant[] }) => {
      if (payload && typeof payload.count === 'number') {
        setCollaboratorCount(Math.max(1, payload.count));
      }

      if (Array.isArray(payload?.participants)) {
        setParticipants(payload.participants);
        setCollaboratorCount(Math.max(1, payload.participants.length));
      }
    });

    socket.on('unavailable', () => {
      // when we connect we need to check to make sure the
      // drawing isn't being used
      // we need to show a message that the site location is now being used
      setCenterMessage('Whoops! Looks like someone is using this map...');
      // then redirect
      setDrawSiteId({ siteId: '', siteName: '' });
      setTimeout(() => navigate('/map-whiteboard'), 3000);
    });
    socket.on('available', () => {
      // when we connect we need to check to make sure the
      // drawing isn't being used
      setIsInDrawModeStatusChange(true);
      needToExitDrawMode.current = true;
    });

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      handleCleanUp();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  const handleCleanUp = () => {
    window.removeEventListener('visibilitychange', handleVisibilityChange);
    if (isInDrawModeStatusChange) {
      setDrawSiteId({ siteId: '', siteName: '' });
    }
  };

  if (isInDrawModeStatusChange) {
    return (
      <CanvasDrawing
        siteId={siteId}
        siteName={siteName}
        collaboratorCount={collaboratorCount}
        participants={participants}
        socket={socketRef.current || undefined}
        snapshot={snapshotData}
        queuedOps={queuedOps}
      />
    );
  }
  return (
    <Box height={'88vh'} width={'92vw'} display={'grid'} sx={{ placeItems: 'center' }} bgcolor={'lightgray'}>
      <Typography>{centerMessage}</Typography>
    </Box>
  );
};

export default CanvasDrawingPage;
